import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Removes non-ASCII characters from a job title string.
 * This helps sanitize input that may include emojis, smart quotes, or hidden characters.
 *
 * @param {string} job_title - The raw job title string
 * @returns {string} - The cleaned job title with only printable ASCII characters
 */
export const cleanJobTitle = (job_title) => {
    return job_title.replaceAll(/[^(\x20-\x7F)\x0A\x0D]*/gi, '');
};

/**
 * Normalizes and validates a postal code (ZIP) value.
 * Converts the input to a string and ensures it is numeric only.
 * Returns the original ZIP if valid, or '0' if invalid or missing.
 *
 * @param {string|number|null|undefined} zip - The raw ZIP or postal code input
 * @returns {string} - A sanitized ZIP code (e.g. "32204") or fallback "0"
 */
export const sanitizePostal = (zip) => {
    zip = String(zip ?? '');
    return /^[0-9]+$/.test(zip) ? zip : '0';
};

/**
 * Normalizes a string for use in SEO-friendly URLs (slugs).
 * - Converts to lowercase
 * - Removes special characters except letters, numbers, spaces, dashes, and underscores
 * - Collapses multiple spaces/dashes into a single space
 * - Trims whitespace
 * - Replaces spaces and underscores with hyphens
 *
 * @param {string|null|undefined} str - The input string to normalize
 * @returns {string} - A clean, hyphenated slug-safe string
 */
const normalizeSegment = (str) => {
    return String(str ?? '')
        .toLowerCase()
        .replaceAll(/[^a-z0-9_\s-]/gi, '')
        .replaceAll(/[\s-]+/gi, ' ')
        .trim()
        .replaceAll(/[\s_]/gi, '-');
};

/**
 * Constructs a canonical job detail URL for use in links and routing.
 * Applies normalization and sanitation to each segment to ensure safe, SEO-friendly output.
 *
 * Example Output:
 * /teletherapy-jobs/speech-language-pathologist-jacksonville-fl/1234567-32204/j/?detail_cat=2000020
 *
 * @param {string} job_category - Job category name (e.g., "Speech Language Pathologist")
 * @param {string} job_city - City name (e.g., "Jacksonville")
 * @param {string} job_state - State abbreviation (e.g., "FL")
 * @param {string|number|null} job_zip - ZIP code (e.g., "32204" or "none")
 * @param {string|number|null} job_id - Raw job ID (e.g., "1234567")
 * @param {string|number|false} job_category_id - Optional category ID to append as a query param
 * @returns {string} - A full job detail URL
 */
export const buildJobURL = (job_category, job_city, job_state, job_zip, job_id, job_category_id = false) => {
    job_category = normalizeSegment(job_category);
    job_city = normalizeSegment(job_city);
    job_state = normalizeSegment(job_state);
    job_zip = sanitizePostal(job_zip);
    job_id = job_id ?? '';

    const query = job_category_id ? `?detail_cat=${encodeURIComponent(job_category_id)}` : '';

//    return `/pharmacy-jobs/${job_category}-${job_city}-${job_state}/${job_id}-${job_zip}/j/${query}`;
    return `/pharmacy-jobs/${job_category}-${job_city}-${job_state}/i/${job_id}-${job_zip}/${query}`;

};

/**
 * Cleans and normalizes job description HTML for safe display and structured data usage.
 * - Removes unwanted tags (table-related, <h1>)
 * - Strips attributes from most tags, except <a> and basic inline tags
 * - Adds missing `mailto:` to email hrefs
 * - Converts `http://rphonthego.com` links to `https://www.rphonthego.com`
 *
 * @param {string} job_desc - Raw HTML job description
 * @returns {string} - Cleaned and normalized HTML
 */
export const cleanJobDescHTML = (job_desc) => {
    function convert_domain_http_to_https(desc, domain) {
        const pattern = new RegExp(
            'href="http://(www\\.)?' + domain.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&') + '([^"]*)"',
            'gi'
        );

        return desc.replace(pattern, (match, wwwPrefix, path) => {
            const wwwDomain = wwwPrefix ? domain : 'www.' + domain;
            return `href="https://${wwwDomain}${path}"`;
        });
    }

    let job_desc_cleaned = String(job_desc ?? '');

    // Remove <table>, <tbody>, <tr>, <td>, and <h1> tags entirely
    job_desc_cleaned = job_desc_cleaned.replaceAll(/\<\/?(table|tbody|tr|td|h1)([^>]*)\>/gi, '');

    // Strip attributes from most HTML tags (excluding <a>)
    job_desc_cleaned = job_desc_cleaned.replaceAll(/<([b-z][a-z0-9]*)[^>]*?(\/?)>/gis, '<$1$2>');

    // Convert any <u> tags to a span.decor-underline
    job_desc_cleaned = job_desc_cleaned.replace(
        /<u>([\s\S]*?)<\/u>/gi,
        '<span class="decor-underline">$1</span>'
    );

    // Fix email links missing "mailto:"
    job_desc_cleaned = job_desc_cleaned.replaceAll(
        /href=\"([a-z0-9._%+-]+)@([a-z0-9.-]+\.[a-z]{2,})\"/gi,
        'href="mailto:$1@$2"'
    );

    // Convert insecure internal links to secure
    job_desc_cleaned = convert_domain_http_to_https(job_desc_cleaned, 'rphonthego.com');

    return job_desc_cleaned;
};

/**
 * Remove the main category prefix from a job category string, unless it matches specific protected categories.
 *
 * This function is used to strip the leading category from compound job categories like "Schools-SLP",
 * unless the category is one of the protected values (e.g., "Homehealth", "COVID-19", or anything starting with "Physicians-").
 *
 * @param {string} job_category - The original job category string.
 * @returns {string} The cleaned job category string with main prefix removed if applicable.
 */
export const removeMainCat = (job_category) => {
    let cleaned_job_cat = job_category;

    try {
        if (!cleaned_job_cat.match(/(^|\s)(Homehealth|COVID-19|Physicians-.*?)(\s|$)/gi))
            cleaned_job_cat = /-(.+)/.exec(job_category)[1];
    } catch {}

    return cleaned_job_cat.trim();
};

/**
 * Format a date string into a readable long format (e.g., "April 15, 2025").
 *
 * @param {string} date_string - A valid date string (e.g., ISO format).
 * @returns {string} Formatted date string.
 */
export const formatDate = (date_string) => {
    let date = new Date(date_string),
        year = date.getFullYear(),
        month = date.toLocaleString('default', { month: 'long' }),
        day = date.getDate();

    return `${month} ${day}, ${year}`;
};

/**
 * Format a date string into a short MM/DD format (e.g., "04/15").
 * This is typically used when displaying a date without the year.
 *
 * @param {string} date_string - A valid date string (e.g., ISO format).
 * @returns {string} Short formatted date string.
 */
export const formatShortDate = (date_string) => {
    let date = new Date(date_string),
        year = date.getFullYear().toString().substr(-2),
        month = date.toLocaleString('default', { month: '2-digit' }),
        day = date.toLocaleString('default', { day: '2-digit' });

    return `${month}/${day}`;
};

export const getFullState = (state_abbr) => {
    let states = {
        AL: 'Alabama',
        AK: 'Alaska',
        AZ: 'Arizona',
        AR: 'Arkansas',
        CA: 'California',
        CO: 'Colorado',
        CT: 'Connecticut',
        DE: 'Delaware',
        DC: 'District Of Columbia',
        FL: 'Florida',
        GA: 'Georgia',
        GU: 'Guam',
        HI: 'Hawaii',
        ID: 'Idaho',
        IL: 'Illinois',
        IN: 'Indiana',
        IA: 'Iowa',
        KS: 'Kansas',
        KY: 'Kentucky',
        LA: 'Louisiana',
        ME: 'Maine',
        MD: 'Maryland',
        MA: 'Massachusetts',
        MI: 'Michigan',
        MN: 'Minnesota',
        MS: 'Mississippi',
        MO: 'Missouri',
        MT: 'Montana',
        NE: 'Nebraska',
        NV: 'Nevada',
        NH: 'New Hampshire',
        NJ: 'New Jersey',
        NM: 'New Mexico',
        NY: 'New York',
        NC: 'North Carolina',
        ND: 'North Dakota',
        OH: 'Ohio',
        OK: 'Oklahoma',
        OR: 'Oregon',
        PW: 'Palau',
        PA: 'Pennsylvania',
        PR: 'Puerto Rico',
        RI: 'Rhode Island',
        SC: 'South Carolina',
        SD: 'South Dakota',
        TN: 'Tennessee',
        TX: 'Texas',
        UT: 'Utah',
        VT: 'Vermont',
        VI: 'Virgin Islands',
        VA: 'Virginia',
        WA: 'Washington',
        WV: 'West Virginia',
        WI: 'Wisconsin',
        WY: 'Wyoming',
    };

    return states[state_abbr] !== undefined ? states[state_abbr] : state_abbr;
};

export const getAbbrState = (full_state) => {
    let states = {
        'Alabama': 'AL',
        'Alaska': 'AK',
        'Arizona': 'AZ',
        'Arkansas': 'AR',
        'California': 'CA',
        'Colorado': 'CO',
        'Connecticut': 'CT',
        'Delaware': 'DE',
        'District Of Columbia': 'DC',
        'Florida': 'FL',
        'Georgia': 'GA',
        'Guam': 'GU',
        'Hawaii': 'HI',
        'Idaho': 'ID',
        'Illinois': 'IL',
        'Indiana': 'IN',
        'Iowa': 'IA',
        'Kansas': 'KS',
        'Kentucky': 'KY',
        'Louisiana': 'LA',
        'Maine': 'ME',
        'Marshall Islands': 'MH',
        'Maryland': 'MD',
        'Massachusetts': 'MA',
        'Michigan': 'MI',
        'Minnesota': 'MN',
        'Mississippi': 'MS',
        'Missouri': 'MO',
        'Montana': 'MT',
        'Nebraska': 'NE',
        'Nevada': 'NV',
        'New Hampshire': 'NH',
        'New Jersey': 'NJ',
        'New Mexico': 'NM',
        'New York': 'NY',
        'North Carolina': 'NC',
        'North Dakota': 'ND',
        'Ohio': 'OH',
        'Oklahoma': 'OK',
        'Oregon': 'OR',
        'Palau': 'PW',
        'Pennsylvania': 'PA',
        'Puerto Rico': 'PR',
        'Rhode Island': 'RI',
        'South Carolina': 'SC',
        'South Dakota': 'SD',
        'Tennessee': 'TN',
        'Texas': 'TX',
        'Utah': 'UT',
        'Vermont': 'VT',
        'Virgin Islands': 'VI',
        'Virginia': 'VA',
        'Washington': 'WA',
        'West Virginia': 'WV',
        'Wisconsin': 'WI',
        'Wyoming': 'WY',
    };

    return states[full_state] !== undefined ? states[full_state] : full_state;
};

export const sortFacet = (arr) => {
    arr.sort((a, b) => {
        const nameA = a.name.toUpperCase(); // ignore upper and lowercase
        const nameB = b.name.toUpperCase(); // ignore upper and lowercase
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }

        // names must be equal
        return 0;
    });

    return arr;
};

export const FriendlyFacetURLs = (predefined_cats, state = null, city = null) => {
    let final_url = 'javascript:void(0);';

    if (predefined_cats.length > 0) {
        final_url = window.location.pathname;

        if (final_url.includes('/c-')) final_url = final_url.split('/c-')[0] + '/'; //remove anything after "/c-"
        if (final_url.includes('/s-')) final_url = final_url.split('/s-')[0] + '/'; //remove anything after "/s-"

        if (city !== null) {
            city = city.toLowerCase().trim().replaceAll(/[\s_]/gi, '-');
            final_url += `c-${city}-`;
        }

        if (state !== null) {
            state = state.toLowerCase().trim().replaceAll(/[\s_]/gi, '-');
            final_url += `s-${state}/`;
        }
    }

    return final_url;
};

/**
 * Custom hook to remove the 'pg' (pagination) query parameter from the current URL.
 *
 * Useful for resetting pagination state when filters or search criteria change.
 *
 * @returns {Function} A function that, when called, removes '?pg=' from the URL using React Router.
 */
export const useRemovePageQSFromURL = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const removePageQSFromURL = () => {
        const searchParams = new URLSearchParams(location.search);

        // If the 'pg' parameter exists, remove it
        if (searchParams.has('pg')) {
            searchParams.delete('pg'); // Remove 'pg' from the search params

            // Construct the new query string (excluding 'pg')
            const newSearchString = searchParams.toString();

            // Use navigate to update the URL with the modified query string
            navigate(
                {
                    search: newSearchString ? `?${newSearchString}` : '',
                },
                { replace: true }
            );
        }
    };

    return removePageQSFromURL; // Return the function to call it in components
};

/**
 * Smoothly scrolls to the top of the job search section depending on screen size.
 * Falls back to scrolling to the top of the page if no target element is found.
 */
export const scrollToTopOfJobs = () => {
    const isDesktop = window.innerWidth >= 992;

    const targetElement = isDesktop
        ? document.getElementById('header-job-search')
        : document.getElementById('SearchSubmit');

    if (targetElement) {
        targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    } else {
        // Fallback: scroll to top of page if no element is found
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    }
};

/**
 * Smoothly scrolls to a target DOM element with optional offset adjustments.
 *
 * @param {string} selector - A valid CSS selector for the element to scroll to.
 * @param {number} [extraOffset=0] - Additional pixels to offset the scroll position.
 */
export const scrollToTarget = (selector, extraOffset = 0) => {
    const element = document.querySelector(selector);
    if (!element) return;

    const siteHeader = document.querySelector('.site-header');
    const stickyHeaderActive = siteHeader?.classList.contains('sticky');
    const adminBarActive = document.body.classList.contains('admin-bar'); // WordPress admin bar

    const stickyHeaderHeight = stickyHeaderActive ? siteHeader.offsetHeight : 0;
    const adminBarHeight = adminBarActive ? 32 : 0; // WordPress admin bar default height
    const offset = extraOffset + stickyHeaderHeight + adminBarHeight;

    const elementY = element.getBoundingClientRect().top + window.pageYOffset - offset;

    window.scrollTo({
        top: elementY,
        behavior: 'smooth',
    });
};

/**
 * Calculate the number of days between a given date and today, and return a human-readable string.
 *
 * @param {string|Date} date1 - The date to compare (can be a string or Date object).
 * @returns {string} A string like "Today", "1 day ago", or "X days ago".
 */
export const dateDiffInDays = (date1) => {
    const today = new Date();
    const date2 = new Date(today.getFullYear(), today.getMonth(), today.getDate()); // Normalize to midnight

    const diffTime = Math.abs(date2 - new Date(date1));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    let finalString = '';
    if (diffDays === 1) {
        finalString = '1 day ago';
    } else if (diffDays < 1) {
        finalString = 'Today';
    } else {
        finalString = `${diffDays} days ago`;
    }

    return finalString;
};
